import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Video, Plus } from 'lucide-react';
import { PushButton } from '../components/PushButton';

const fmtDate = (iso) => new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });

const ConsultCard = ({ c, isDesigner }) => (
  <Link to={`/asesoria/${c.id}`} className="block border border-stilo-ink/10 bg-white p-6 hover:bg-stilo-beige transition-colors" data-testid={`my-consult-${c.id}`}>
    <div className="flex justify-between items-start gap-4">
      <p className="overline">{c.modality === 'live' ? 'Videollamada' : 'Asíncrona'} · {fmtDate(c.created_at)}</p>
      <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 ${c.status === 'closed' ? 'bg-stilo-beige text-stilo-ink2' : 'bg-stilo-ink text-stilo-bg'}`}>{c.status === 'closed' ? 'Finalizada' : 'Activa'}</span>
    </div>
    <p className="font-serif text-2xl mt-3">{c.package_name}</p>
    {isDesigner && <p className="text-sm text-stilo-ink2 mt-1">{c.user_email}</p>}
    {c.scheduled_at && <p className="text-sm text-stilo-ink2 mt-1">Cita: {new Date(c.scheduled_at).toLocaleString('es-ES')}</p>}
    <p className="text-xs text-stilo-ink3 mt-4">{c.message_count} {c.message_count === 1 ? 'mensaje' : 'mensajes'}{c.unread > 0 ? ` · ${c.unread} sin leer` : ''} · Abrir →</p>
    {!isDesigner && c.status === 'closed' && !c.has_review && (
      <p className="mt-3 inline-block text-[10px] tracking-[0.2em] uppercase px-2 py-1 bg-stilo-gold text-stilo-bg" data-testid={`review-pending-${c.id}`}>Valora tu asesoría</p>
    )}
  </Link>
);

const ConvRow = ({ c, isDesigner }) => (
  <Link to={`/asesoria/${c.id}`} className="flex gap-5 items-start py-5 px-3 border-b border-stilo-ink/10 hover:bg-stilo-beige transition-colors" data-testid={`conversation-${c.id}`}>
    <span className="mt-1 w-10 h-10 shrink-0 grid place-items-center bg-stilo-beige text-stilo-ink2">
      {c.modality === 'live' ? <Video className="w-4 h-4" strokeWidth={1.5} /> : <MessageCircle className="w-4 h-4" strokeWidth={1.5} />}
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex justify-between gap-4">
        <p className="font-serif text-xl truncate">{isDesigner ? (c.user_name || c.user_email) : c.package_name}</p>
        <span className="text-xs text-stilo-ink3 whitespace-nowrap flex items-center gap-2">
          {c.unread > 0 && <span className="bg-stilo-gold text-stilo-bg text-[10px] px-2 py-0.5 rounded-full" data-testid={`unread-badge-${c.id}`}>{c.unread} {c.unread === 1 ? 'nuevo' : 'nuevos'}</span>}
          {fmtDate(c.last_activity)}
        </span>
      </div>
      {isDesigner && <p className="overline mt-1">{c.package_name}</p>}
      <p className="text-sm text-stilo-ink2 mt-1 truncate">
        {c.last_message ? `${c.last_message.author_role === 'designer' ? 'Studio Stilo' : c.last_message.author_name}: ${c.last_message.preview}` : 'Sin mensajes todavía'}
      </p>
    </div>
  </Link>
);

const Group = ({ title, items, empty, children, testid }) => (
  <section className="mt-12" data-testid={testid}>
    <h2 className="font-serif text-3xl">{title} <span className="text-stilo-ink3 text-xl">({items.length})</span></h2>
    {items.length === 0 ? <p className="mt-4 text-stilo-ink3">{empty}</p> : children}
  </section>
);

export default function MyConsultations({ mode }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const isDesigner = user?.role === 'designer';
  useEffect(() => { api.get('/conversations').then(r => setItems(r.data)); }, []);

  const conv = mode === 'conversaciones';
  const active = (items || []).filter(c => c.status !== 'closed');
  const past = (items || []).filter(c => c.status === 'closed');
  const sorted = conv ? null : [...(items || [])].sort((a, b) => b.created_at.localeCompare(a.created_at));

  return (
    <main className="bg-stilo-bg min-h-[70vh]" data-testid={conv ? 'conversations-page' : 'my-consultations-page'}>
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 pt-16 pb-32">
        <p className="overline">{isDesigner ? 'Panel Studio Stilo' : 'Tu espacio privado'}</p>
        <div className="flex flex-wrap justify-between items-end gap-6 mt-3">
          <h1 className="font-serif text-5xl lg:text-6xl leading-[0.95]">{conv ? 'Conversaciones' : (isDesigner ? 'Asesorías contratadas' : 'Mis asesorías')}</h1>
          {!isDesigner && <Link to="/asesorias" className="btn-stilo" data-testid="book-new-consultation"><Plus className="w-4 h-4" strokeWidth={1.5} /> Reservar nueva</Link>}
        </div>
        <PushButton className="mt-6" label="Avísame en el móvil de mensajes nuevos" testid="conversations-push-toggle" />
        {items === null && <p className="mt-12 text-stilo-ink2">Cargando…</p>}
        {items && !conv && (
          <Group title="Contratadas" items={sorted} empty="Aún no tienes asesorías contratadas." testid="consultations-list">
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">{sorted.map(c => <ConsultCard key={c.id} c={c} isDesigner={isDesigner} />)}</div>
          </Group>
        )}
        {items && conv && (
          <>
            <Group title="Activas" items={active} empty="No hay conversaciones activas." testid="conversations-active">
              <div className="mt-4 border-t border-stilo-ink/10">{active.map(c => <ConvRow key={c.id} c={c} isDesigner={isDesigner} />)}</div>
            </Group>
            <Group title="Pasadas" items={past} empty="Todavía no hay conversaciones finalizadas." testid="conversations-past">
              <div className="mt-4 border-t border-stilo-ink/10 opacity-80">{past.map(c => <ConvRow key={c.id} c={c} isDesigner={isDesigner} />)}</div>
            </Group>
          </>
        )}
      </div>
    </main>
  );
}
